import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft, Check, X } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { mobilePageBackground } from '../../../services/mobileStyleService';
import { getBaseballBackground } from './baseballAssets';

const MobileSignUpStep2 = ({ onSignUp, onBack, formData, loading, error }) => {
  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    numberOrSpecial: false
  });

  // Check password requirements
  useEffect(() => {
    const pwd = passwords.password;
    setPasswordStrength({
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      numberOrSpecial: /[0-9!@#$%^&*(),.?":{}|<>]/.test(pwd)
    });
  }, [passwords.password]);

  const handleChange = (e) => {
    setPasswords({
      ...passwords,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (passwords.password !== passwords.confirmPassword) {
      return; // Error will be shown by the UI
    }

    // Check all requirements met
    const allRequirementsMet = Object.values(passwordStrength).every(v => v === true);
    if (!allRequirementsMet) {
      return; // Error will be shown by the UI
    }

    // Combine with step 1 data
    onSignUp({
      ...formData,
      password: passwords.password,
      confirmPassword: passwords.confirmPassword
    });
  };

  const passwordsMatch = passwords.confirmPassword === '' || passwords.password === passwords.confirmPassword;
  const allRequirementsMet = Object.values(passwordStrength).every(v => v === true);
  const canSubmit = allRequirementsMet && passwordsMatch && passwords.confirmPassword !== '';

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: mobilePageBackground, ...getBaseballBackground() }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${dynastyTheme.classes.border.neutral}`}>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <h2 className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
          Join the League
        </h2>
        <div className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
          2/2
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
          {/* Welcome Message */}
          <div className="text-center mb-8">
            <div className="text-4xl mb-3">🔒</div>
            <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
              Create Your Password
            </h3>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
              Keep your account secure
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className={`${dynastyTheme.components.badge.error} p-4 rounded-lg text-center`}>
              {error}
            </div>
          )}

          {/* Password */}
          <div>
            <label className={dynastyTheme.components.label}>
              Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={passwords.password}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-12 py-3 text-base`}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          {passwords.password && (
            <div className={`rounded-lg p-4 space-y-2 ${dynastyTheme.classes.bg.neutral} border ${dynastyTheme.classes.border.neutral}`}>
              <p className={`text-sm font-semibold ${dynastyTheme.classes.text.white} mb-2`}>
                Password must have:
              </p>
              
              <RequirementItem 
                met={passwordStrength.length}
                text="At least 8 characters"
              />
              <RequirementItem 
                met={passwordStrength.uppercase}
                text="Uppercase letter (A-Z)"
              />
              <RequirementItem 
                met={passwordStrength.lowercase}
                text="Lowercase letter (a-z)"
              />
              <RequirementItem 
                met={passwordStrength.numberOrSpecial}
                text="Number or special character"
              />
            </div>
          )}

          {/* Confirm Password */}
          <div>
            <label className={dynastyTheme.components.label}>
              Confirm Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handleChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-12 py-3 text-base ${
                  !passwordsMatch ? 'border-red-500' : ''
                }`}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {!passwordsMatch && passwords.confirmPassword && (
              <p className={`text-sm ${dynastyTheme.classes.text.error} mt-2 flex items-center gap-1`}>
                <X className="w-4 h-4" />
                Passwords do not match
              </p>
            )}
          </div>

          {/* Create Account Button */}
          <button
            type="submit"
            disabled={loading || !canSubmit}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'lg')} w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <>Creating Account...</>
            ) : (
              <>
                <span>⚾</span>
                Create Account
                <span>⚾</span>
              </>
            )}
          </button>

          {/* Terms */}
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLighter} text-center`}>
            By signing up, you agree to our{' '}
            <span className={dynastyTheme.classes.text.primary}>Terms of Service</span>
            {' '}and{' '}
            <span className={dynastyTheme.classes.text.primary}>Privacy Policy</span>
          </p>
        </form>
      </div>
    </div>
  );
};

// Helper component for password requirements
const RequirementItem = ({ met, text }) => {
  return (
    <div className="flex items-center gap-2">
      {met ? (
        <Check className={`w-4 h-4 ${dynastyTheme.classes.text.success}`} />
      ) : (
        <X className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLighter}`} />
      )}
      <span className={`text-sm ${met ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.neutralLighter}`}>
        {text}
      </span>
    </div>
  );
};

export default MobileSignUpStep2;
